// Painel de anotações dentro do caderno (AiStudyView).
// [[project-notes-system-caderno]] — MVP busca interna no texto + tags.
//
// Layout: cabeçalho com busca, lista de notas, editor inline. Notas pinned
// vêm no topo. Salvar é por blur + debounce 800ms enquanto digita (auto-save).

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  AlertTriangle,
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowLeftRight,
  Bold,
  ChevronDown,
  Link as LinkIcon,
  PanelLeftClose,
  PanelLeftOpen,
  Code,
  Code2,
  Copy,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Image as ImageIcon,
  Info,
  Italic,
  Lightbulb,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Loader2,
  Maximize2,
  Minimize2,
  Minus,
  Palette,
  PictureInPicture2,
  Pin,
  PinOff,
  Plus,
  Quote,
  RefreshCw,
  RemoveFormatting,
  FileDown,
  Search,
  CalendarClock,
  ExternalLink,
  FileText,
  Share2,
  Sigma,
  Sparkles,
  SquarePen,
  StickyNote,
  Strikethrough,
  Table as TableIcon,
  Trash2,
  Underline,
  Upload,
  X
} from "lucide-react";
import { api, call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { openAlert, openConfirm, openPrompt } from "./ConfirmPromptModal";
import { AiMarkdownBlock } from "./aiMarkdown";
import { openShareDialog } from "./ShareDialog";
import { renderBlock as renderMdBlock } from "@app/ui/lib/markdown";
import { ensureKaTeX, renderMathIn } from "@app/ui/lib/math";
import { flagEnabled } from "@app/ui/lib/feature-flags";
import { openNoteInWindow, useSingleNoteWindowOpen } from "./SingleNoteWindow";

export interface Note {
  id: string;
  title: string;
  contentMd: string;
  tags: string[];
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Exporta as anotações deste target como PDF — abre iframe oculto com o HTML
 *  gerado pelo /api/student/notes-export-pdf e chama window.print().
 *  [[project-notes-system-caderno]] */
async function exportNotesPdf(
  targetKind: string,
  targetId: string,
  noteCount: number
): Promise<void> {
  if (noteCount === 0) return;
  const params = new URLSearchParams();
  params.set("targetKind", targetKind);
  params.set("targetId", targetId);
  await runNotesPdfPrint(params);
}

/** Mesmo fluxo do exportNotesPdf mas pra UMA nota específica. Usado pelo
 *  popout SingleNoteWindow e pelo 3-pontinhos no hub "Minhas anotações". */
export async function exportSingleNotePdf(noteId: string): Promise<void> {
  if (!noteId) return;
  const params = new URLSearchParams();
  params.set("ids", noteId);
  await runNotesPdfPrint(params);
}

async function runNotesPdfPrint(params: URLSearchParams): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api/student/notes-export-pdf?${params.toString()}`, {
      credentials: "include"
    });
  } catch {
    await openAlert({
      title: "Sem conexão",
      message: "Não consegui falar com o servidor pra gerar o PDF. Verifique sua internet e tente de novo.",
      confirmLabel: "Ok"
    });
    return;
  }
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      msg = body?.error?.message ?? msg;
    } catch {
      /* ignora */
    }
    await openAlert({
      title: "Não consegui exportar",
      message: msg,
      confirmLabel: "Ok"
    });
    return;
  }
  const html = await res.text();

  const prev = document.getElementById("_notesPdfPrintFrame");
  if (prev) prev.remove();
  const iframe = document.createElement("iframe");
  iframe.id = "_notesPdfPrintFrame";
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed; right:0; bottom:0; width:0; height:0; border:0; visibility:hidden;";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    await openAlert({
      title: "Falha",
      message: "Não consegui criar a área de impressão. Tente recarregar a página.",
      confirmLabel: "Ok"
    });
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  // Espera imagens + KaTeX terminarem antes de printar
  await new Promise<void>((resolve) => {
    let fired = false;
    const trigger = () => {
      if (fired) return;
      fired = true;
      const imgs = doc.querySelectorAll("img");
      let pending = imgs.length;
      const fire = () => {
        setTimeout(() => {
          try {
            const prevTitle = document.title;
            document.title = "Anotações";
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            document.title = prevTitle;
          } catch {
            /* ignore */
          }
          resolve();
        }, 350);
      };
      if (pending === 0) {
        fire();
        return;
      }
      imgs.forEach((img) => {
        if ((img as HTMLImageElement).complete) {
          pending -= 1;
          if (pending === 0) fire();
        } else {
          img.addEventListener("load", () => {
            pending -= 1;
            if (pending === 0) fire();
          });
          img.addEventListener("error", () => {
            pending -= 1;
            if (pending === 0) fire();
          });
        }
      });
    };
    if (iframe.contentDocument?.readyState === "complete") {
      trigger();
    } else {
      iframe.addEventListener("load", trigger, { once: true });
      setTimeout(trigger, 4000); // fallback caso load não dispare
    }
  });
}

interface NotesPanelProps {
  targetKind: "ai_study_session" | "bank" | "question";
  targetId: string;
  /** Opcional: callback que troca o lado da lateral no parent (AiStudyView).
   *  Quando provido, renderiza um botão ↔ no header. */
  onSwapSide?: () => void;
}

export function CadernoNotesPanel({ targetKind, targetId, onSwapSide }: NotesPanelProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  // Coluna de títulos pode ser recolhida quando o painel está em coluna
  // estreita (ex: side-panel do Modo Estudo / Caderno). Persistido em
  // localStorage pra lembrar a escolha do aluno entre sessões.
  const [listCollapsed, setListCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("caderno.notes.listCollapsed") === "1";
    } catch {
      return false;
    }
  });
  function toggleList() {
    setListCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem("caderno.notes.listCollapsed", next ? "1" : "0");
      } catch {}
      return next;
    });
  }

  async function load() {
    setPhase("loading");
    try {
      const params = new URLSearchParams({ targetKind, targetId });
      const res = await call<{ notes: Note[] }>(
        `/api/student/notes?${params.toString()}`
      );
      setNotes(res.notes);
      // Mantém ativo se ainda existe; senão escolhe o primeiro.
      if (activeId && !res.notes.some((n) => n.id === activeId)) {
        setActiveId(res.notes[0]?.id ?? null);
      } else if (!activeId && res.notes.length > 0) {
        setActiveId(res.notes[0].id);
      }
      setPhase("ready");
    } catch (e) {
      setErr(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, [targetKind, targetId]);

  async function createNew() {
    try {
      const res = await call<{ note: Note }>("/api/student/notes", {
        method: "POST",
        body: JSON.stringify({
          targetKind,
          targetId,
          title: "Nova anotação",
          contentMd: ""
        })
      });
      setNotes((prev) => [res.note, ...prev]);
      setActiveId(res.note.id);
    } catch (e) {
      setErr(friendlyError(e));
    }
  }

  async function patchNote(id: string, patch: Partial<Note>) {
    try {
      const res = await call<{ note: Note }>(
        `/api/student/notes?id=${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify(patch) }
      );
      setNotes((prev) => prev.map((n) => (n.id === id ? res.note : n)));
    } catch (e) {
      setErr(friendlyError(e));
    }
  }

  async function deleteNote(id: string) {
    const note = notes.find((n) => n.id === id);
    const titleEcho = note?.title?.trim() ? ` "${note.title}"` : "";
    // Confirmação reforçada: usuário precisa digitar APAGAR. Evita exclusão
    // acidental por clique duplo / muscle memory.
    const typed = await openPrompt({
      title: `Apagar anotação${titleEcho}?`,
      message:
        "Essa ação é permanente — a anotação será removida e não pode ser recuperada. Para confirmar, digite APAGAR abaixo.",
      placeholder: "APAGAR",
      confirmLabel: "Apagar definitivamente",
      cancelLabel: "Cancelar",
      requirePhrase: "APAGAR",
      danger: true
    });
    if (typed == null) return;
    try {
      await call(`/api/student/notes?id=${encodeURIComponent(id)}`, {
        method: "DELETE"
      });
      const remaining = notes.filter((n) => n.id !== id);
      setNotes(remaining);
      if (activeId === id) {
        setActiveId(remaining[0]?.id ?? null);
      }
    } catch (e) {
      setErr(friendlyError(e));
    }
  }

  // Filtro client-side: substring no título, tags e conteúdo.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) => {
      // Texto puro (sem tags HTML / marcadores md) pra busca não falsear em
      // pedaços de `<p>`, `<strong>` etc.
      const hay =
        n.title.toLowerCase() +
        " " +
        noteContentToPreview(n.contentMd, 4000).toLowerCase() +
        " " +
        n.tags.join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [notes, search]);

  const active = filtered.find((n) => n.id === activeId) ?? filtered[0] ?? null;

  return (
    <div class="flex h-full min-h-0 flex-col rounded-md border border-border bg-card/40">
      <header class="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div class="flex min-w-0 items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <StickyNote className="size-3.5 shrink-0" />
          <span class="truncate">Anotações ({notes.length})</span>
        </div>
        <div class="flex shrink-0 items-center gap-1">
          {onSwapSide && (
            <button
              type="button"
              onClick={onSwapSide}
              title="Trocar lado da lateral"
              aria-label="Trocar lado"
              className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ArrowLeftRight className="size-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={toggleList}
            title={listCollapsed ? "Mostrar lista de títulos" : "Recolher lista de títulos"}
            aria-label={listCollapsed ? "Mostrar lista" : "Recolher lista"}
            className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {listCollapsed ? (
              <PanelLeftOpen className="size-3.5" />
            ) : (
              <PanelLeftClose className="size-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => void exportNotesPdf(targetKind, targetId, notes.length)}
            disabled={notes.length === 0}
            title={
              notes.length === 0
                ? "Sem anotações pra exportar"
                : "Salvar todas as anotações deste caderno em PDF"
            }
            aria-label="Exportar anotações em PDF"
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
          >
            <FileDown className="size-3" />
            PDF
          </button>
          <button
            type="button"
            onClick={createNew}
            title="Nova anotação"
            aria-label="Nova anotação"
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
          >
            <Plus className="size-3" />
            Nova
          </button>
        </div>
      </header>

      <div class="px-2 pt-2">
        <div class="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            placeholder="Buscar no texto…"
            aria-label="Buscar anotações"
            class="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-7 text-xs outline-none focus:border-primary"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Limpar busca"
              title="Limpar"
              class="absolute right-1 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
      </div>

      {err && (
        <div class="mx-2 mt-2 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-[10px] text-destructive">
          <AlertCircle className="mr-1 inline size-3" /> {err}
        </div>
      )}

      <div class="flex min-h-0 flex-1">
        {listCollapsed ? null : (
        <ul class="w-32 max-w-[40%] shrink-0 space-y-1 overflow-y-auto border-r border-border p-2 sm:w-40">
          {phase === "loading" && (
            <li class="px-2 py-2 text-[11px] text-muted-foreground">
              <Loader2 className="mr-1 inline size-3 animate-spin" /> Carregando…
            </li>
          )}
          {phase === "ready" && filtered.length === 0 && (
            <li class="px-2 py-2 text-center text-[11px] text-muted-foreground">
              {search ? "Sem resultados." : "Nenhuma anotação ainda."}
            </li>
          )}
          {filtered.map((n) => {
            const isActive = active?.id === n.id;
            return (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => setActiveId(n.id)}
                  class={`flex w-full items-center gap-1 rounded px-2 py-1.5 text-left text-[11px] ${
                    isActive
                      ? "bg-primary/15 font-semibold text-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                  title={n.title || "(sem título)"}
                >
                  {n.pinned && <Pin className="size-3 shrink-0 text-amber-500" />}
                  <span class="truncate">{n.title || "(sem título)"}</span>
                </button>
              </li>
            );
          })}
        </ul>
        )}

        <div class="flex min-w-0 flex-1 flex-col">
          {active ? (
            <NoteEditor
              key={active.id}
              note={active}
              onPatch={(p) => void patchNote(active.id, p)}
              onDelete={() => void deleteNote(active.id)}
            />
          ) : (
            <div class="grid flex-1 place-items-center px-4 text-center text-[11px] text-muted-foreground">
              {phase === "ready"
                ? "Crie uma anotação pra começar."
                : "Carregando…"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function NoteEditor({
  note,
  onPatch,
  onDelete,
  isPopout = false
}: {
  note: Note;
  onPatch: (p: Partial<Note>) => void;
  onDelete: () => void;
  /** Quando esse NoteEditor RODA DENTRO da janela popout (SingleNoteWindow),
   *  passamos true. Isso desliga o guard de "anotação aberta em outra janela"
   *  pra não detectar o próprio heartbeat dele mesmo e renderizar o banner. */
  isPopout?: boolean;
}) {
  const [title, setTitle] = useState(note.title);
  const [contentMd, setContentMd] = useState(note.contentMd);
  const [tagsRaw, setTagsRaw] = useState(note.tags.join(", "));
  const [showFlashcards, setShowFlashcards] = useState(false);
  const [focusMode, setFocusMode] = useState(false);

  // Detecta se a anotação está aberta numa janela avulsa (popout via
  // PictureInPicture2 button OU vindo do hub). Quando estiver, o editor
  // inline fica read-only pra evitar edição concorrente que conflitaria
  // no auto-save. [[project-notes-hub-popout]]
  // Quando ESTE editor JÁ É a janela popout (isPopout=true), nunca trava —
  // senão ele detectaria o próprio heartbeat e se travaria sozinho.
  const windowDetected = useSingleNoteWindowOpen(note.id);
  const openInWindow = !isPopout && windowDetected;

  // Estado do auto-save: 'clean' = nada pendente; 'dirty' = aguardando
  // próximo debounce; 'saving' = PATCH em vôo; 'saved' = sucesso recente
  // (volta pra 'clean' em 2s).
  const [saveState, setSaveState] = useState<"clean" | "dirty" | "saving" | "saved">("clean");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef({
    title: note.title,
    contentMd: note.contentMd,
    tags: note.tags.join(", ")
  });

  // Reset quando troca de nota.
  useEffect(() => {
    setTitle(note.title);
    setContentMd(note.contentMd);
    setTagsRaw(note.tags.join(", "));
    lastSentRef.current = {
      title: note.title,
      contentMd: note.contentMd,
      tags: note.tags.join(", ")
    };
  }, [note.id]);

  // Auto-save debounced (800ms).
  useEffect(() => {
    // Compara contra última versão enviada — se nada mudou, mantém 'clean'.
    const last = lastSentRef.current;
    const hasDiff =
      title !== last.title ||
      contentMd !== last.contentMd ||
      tagsRaw !== last.tags;
    if (!hasDiff) return;

    setSaveState("dirty");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const patch: Partial<Note> = {};
      const cur = lastSentRef.current;
      if (title !== cur.title) patch.title = title;
      if (contentMd !== cur.contentMd) patch.contentMd = contentMd;
      if (tagsRaw !== cur.tags) {
        patch.tags = tagsRaw
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
      }
      if (Object.keys(patch).length > 0) {
        lastSentRef.current = { title, contentMd, tags: tagsRaw };
        setSaveState("saving");
        try {
          await Promise.resolve(onPatch(patch));
          setSaveState("saved");
          if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
          savedTimerRef.current = setTimeout(() => setSaveState("clean"), 2000);
        } catch {
          setSaveState("dirty"); // user verá o erro pelo banner do panel
        }
      }
    }, 800);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [title, contentMd, tagsRaw]);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  return (
    <div class="flex min-h-0 flex-1 flex-col">
      <div class="flex items-center gap-1.5 border-b border-border px-2 py-1.5">
        <input
          type="text"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          placeholder="Título…"
          maxLength={200}
          aria-label="Título da anotação"
          class="flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5 text-xs font-bold outline-none focus:border-primary"
        />
        <span
          class={`whitespace-nowrap text-[10px] font-medium transition-opacity ${
            saveState === "clean" ? "opacity-0" : "opacity-100"
          } ${
            saveState === "saving"
              ? "text-amber-600 dark:text-amber-300"
              : saveState === "saved"
              ? "text-emerald-600 dark:text-emerald-300"
              : "text-muted-foreground"
          }`}
          aria-live="polite"
        >
          {saveState === "dirty" && "alterado"}
          {saveState === "saving" && "salvando…"}
          {saveState === "saved" && "salvo ✓"}
        </span>
        {/* Gerar flashcards é gated por feature flag — admin habilita em
            "Feature Flags" do manager-new. Default OFF (custo de IA + decisão
            comercial). [[project-notes-ai-flags]] */}
        {flagEnabled("student.notes.flashcards", false) && (
          <button
            type="button"
            onClick={() => setShowFlashcards(true)}
            title="Gerar flashcards com IA"
            aria-label="Gerar flashcards"
            class="grid size-7 place-items-center rounded text-violet-600 hover:bg-violet-500/10 dark:text-violet-300"
          >
            <Sparkles className="size-3.5" />
          </button>
        )}
        {/* Dentro do popup (isPopout) já estamos numa janela dedicada/fullscreen,
            então Maximize2 e PictureInPicture2 viram redundantes. */}
        {!isPopout && (
          <button
            type="button"
            onClick={() => setFocusMode(true)}
            title="Abrir em foco (tela cheia, nesta aba)"
            aria-label="Abrir em foco"
            class="grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Maximize2 className="size-3.5" />
          </button>
        )}
        {!isPopout && (
          <button
            type="button"
            onClick={() => openNoteInWindow(note.id)}
            title={openInWindow ? "Já está aberta numa janela — clique para focar" : "Abrir em janela avulsa (pop-up)"}
            aria-label="Abrir em janela"
            class={`grid size-7 place-items-center rounded ${
              openInWindow
                ? "text-primary hover:bg-primary/10"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
          >
            <PictureInPicture2 className="size-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() =>
            openShareDialog({
              kind: "nota",
              resourceId: note.id,
              resourceTitle: note.title || "(sem título)"
            })
          }
          title="Compartilhar anotação"
          aria-label="Compartilhar"
          class="grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Share2 className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onPatch({ pinned: !note.pinned })}
          title={note.pinned ? "Desafixar" : "Fixar no topo"}
          aria-label={note.pinned ? "Desafixar" : "Fixar"}
          class={`grid size-7 place-items-center rounded ${
            note.pinned
              ? "text-amber-500 hover:bg-amber-500/10"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          }`}
        >
          {note.pinned ? <Pin className="size-3.5" /> : <PinOff className="size-3.5" />}
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Apagar"
          aria-label="Apagar anotação"
          class="grid size-7 place-items-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-1 border-b border-border bg-muted/30 px-2 py-1 text-[10px]">
        <input
          type="text"
          value={tagsRaw}
          onInput={(e) => setTagsRaw((e.target as HTMLInputElement).value)}
          placeholder="tags separadas por vírgula"
          aria-label="Tags"
          class="ml-auto w-full min-w-0 flex-1 rounded border border-border bg-background px-2 py-0.5 text-[10px] outline-none focus:border-primary sm:w-auto sm:max-w-[14rem] sm:basis-auto"
        />
      </div>

      {/* Editor WYSIWYG — toolbar visual + contentEditable. Sem markdown
          visível pro aluno (pedido 2026-05-20: "nosso usuário é extremamente
          leigo e está acostumado a mexer no Word, não no Notepad++").
          O conteúdo armazenado em contentMd vira HTML quando o aluno edita
          aqui (notas antigas em markdown são convertidas automaticamente
          na primeira abertura). [[project-notes-wysiwyg]] */}
      {openInWindow ? (
        // Edição concorrente é problemática (auto-save sobrescreveria). Mostra
        // um banner com CTA pra focar a janela já aberta. [[project-notes-hub-popout]]
        <div class="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
          <div class="grid size-12 place-items-center rounded-full bg-primary/15 text-primary">
            <PictureInPicture2 className="size-6" />
          </div>
          <div class="space-y-1">
            <p class="text-sm font-semibold text-foreground">
              Esta anotação está aberta em uma janela avulsa.
            </p>
            <p class="text-xs text-muted-foreground">
              Pra evitar que as duas edições se sobreponham, a edição aqui ficou
              suspensa. Volte à janela ou feche-a para continuar editando aqui.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openNoteInWindow(note.id)}
            class="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <PictureInPicture2 className="size-3.5" />
            Trazer janela para frente
          </button>
        </div>
      ) : (
        <RichTextEditor
          key={note.id}
          initialValue={note.contentMd}
          onChange={(html) => setContentMd(html)}
          placeholder="Escreva sua anotação aqui…"
        />
      )}

      {focusMode && (
        <FocusNoteModal
          title={title}
          contentMd={contentMd}
          onClose={() => setFocusMode(false)}
          onChangeContent={(v) => setContentMd(v)}
          onChangeTitle={(v) => setTitle(v)}
        />
      )}

      {showFlashcards && (
        <FlashcardsModal
          noteId={note.id}
          noteTitle={note.title || "(sem título)"}
          contentLength={contentMd.length}
          onClose={() => setShowFlashcards(false)}
        />
      )}
    </div>
  );
}

interface Flashcard {
  front: string;
  back: string;
}

function FlashcardsModal({
  noteId,
  noteTitle,
  contentLength,
  onClose
}: {
  noteId: string;
  noteTitle: string;
  contentLength: number;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<"idle" | "loading" | "ready" | "error">(
    "idle"
  );
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [count, setCount] = useState(8);
  const [focus, setFocus] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const [copiedAt, setCopiedAt] = useState<number | null>(null);

  async function generate() {
    setPhase("loading");
    setErr(null);
    setCards([]);
    setRevealed(new Set());
    try {
      const r = await call<{ ok: true; flashcards: Flashcard[] }>(
        `/api/student/note-flashcards?noteId=${encodeURIComponent(noteId)}`,
        {
          method: "POST",
          body: JSON.stringify({ count, focus: focus.trim() || undefined })
        }
      );
      setCards(r.flashcards);
      setPhase("ready");
    } catch (e) {
      setErr(
        friendlyError(e, {
          overrides: {
            content_too_short:
              "Anotação muito curta pra gerar flashcards. Escreva pelo menos algumas frases primeiro.",
            provider_error:
              "A IA não conseguiu gerar agora. Aguarde alguns segundos e tente de novo.",
            parse_error:
              "A IA devolveu um formato inválido. Tente regerar."
          },
          fallback: "Não foi possível gerar os flashcards agora. Tente de novo."
        })
      );
      setPhase("error");
    }
  }

  function toggle(idx: number) {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  async function copyAll() {
    const txt = cards
      .map((c, i) => `${i + 1}. ${c.front}\n   ↳ ${c.back}`)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(txt);
      setCopiedAt(Date.now());
      setTimeout(() => setCopiedAt(null), 2000);
    } catch {
      /* ignore */
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        class="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-5 py-3">
          <div class="flex items-center gap-2">
            <Sparkles className="size-4 text-violet-500" />
            <h2 class="text-base font-bold">Flashcards com IA</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="space-y-3 border-b border-border px-5 py-3 text-xs">
          <p class="text-muted-foreground">
            A IA vai ler <strong>{noteTitle}</strong> e gerar flashcards
            pergunta/resposta. Use pra revisar antes da prova — copie pra Anki/Quizlet
            ou apenas estude direto aqui.
          </p>
          <div class="flex flex-wrap items-center gap-3">
            <label class="flex items-center gap-2">
              <span class="text-muted-foreground">Quantos cards:</span>
              <input
                type="number"
                min={1}
                max={20}
                value={count}
                onInput={(e) =>
                  setCount(
                    Math.max(1, Math.min(20, Number((e.target as HTMLInputElement).value) || 8))
                  )
                }
                aria-label="Quantidade de flashcards"
                class="w-16 rounded-md border border-border bg-background px-2 py-1 text-sm"
              />
            </label>
            <span class="text-[10px] text-muted-foreground">
              Anotação tem {contentLength} chars
            </span>
          </div>
          <input
            type="text"
            value={focus}
            onInput={(e) => setFocus((e.target as HTMLInputElement).value)}
            placeholder='Foco opcional (ex: "só os mecanismos", "comparações entre X e Y")'
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs"
          />
          <div class="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={generate}
              disabled={phase === "loading" || contentLength < 30}
              class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {phase === "loading" ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : phase === "ready" ? (
                <RefreshCw className="size-3.5" />
              ) : (
                <Sparkles className="size-3.5" />
              )}
              {phase === "ready" ? "Regerar" : "Gerar flashcards"}
            </button>
            {phase === "ready" && cards.length > 0 && (
              <button
                type="button"
                onClick={copyAll}
                class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent"
              >
                <Copy className="size-3.5" />
                {copiedAt ? "Copiado!" : "Copiar todos"}
              </button>
            )}
          </div>
          {contentLength < 30 && (
            <p class="rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-300">
              Anotação muito curta. Escreva pelo menos algumas frases pra IA ter
              o que extrair.
            </p>
          )}
          {err && (
            <p class="rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-[11px] text-destructive">
              <AlertCircle className="mr-1 inline size-3" />
              {err}
            </p>
          )}
        </div>

        <div class="flex-1 overflow-y-auto px-5 py-3">
          {phase === "loading" && (
            <p class="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              IA gerando seus flashcards…
            </p>
          )}
          {phase === "ready" && cards.length === 0 && (
            <p class="py-8 text-center text-xs text-muted-foreground">
              Nenhum flashcard gerado. Tente regerar ou simplificar a anotação.
            </p>
          )}
          {phase === "ready" && cards.length > 0 && (
            <ul class="space-y-2">
              {cards.map((c, i) => {
                const isRevealed = revealed.has(i);
                return (
                  <li
                    key={i}
                    class="overflow-hidden rounded-lg border border-border bg-card"
                  >
                    <button
                      type="button"
                      onClick={() => toggle(i)}
                      class="w-full px-3 py-2.5 text-left text-xs hover:bg-accent/40"
                    >
                      <div class="flex items-start gap-2">
                        <span class="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[10px] font-bold text-violet-700 dark:text-violet-300">
                          {i + 1}
                        </span>
                        <span class="flex-1 font-semibold">{c.front}</span>
                        <span class="text-[10px] text-muted-foreground">
                          {isRevealed ? "ocultar" : "mostrar"}
                        </span>
                      </div>
                    </button>
                    {isRevealed && (
                      <div class="border-t border-border bg-violet-500/5 px-3 py-2.5 text-xs">
                        <AiMarkdownBlock text={c.back} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {phase === "idle" && (
            <p class="py-12 text-center text-xs text-muted-foreground">
              Clique em <strong>Gerar flashcards</strong> pra começar.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FormatToolbar — barra de atalhos de markdown. Cada botão recebe seleção
// atual e devolve novo texto + nova seleção. Não mexe no estado direto;
// chama `onApply(transform)` pro NoteEditor coordenar o flush + restore de
// foco/seleção.
// ============================================================================

type MdTransform = (
  text: string,
  selStart: number,
  selEnd: number
) => { value: string; selectionStart: number; selectionEnd: number };

function wrapSelection(prefix: string, suffix: string, placeholder: string): MdTransform {
  return (text, s, e) => {
    const selected = text.slice(s, e);
    const body = selected || placeholder;
    const value = text.slice(0, s) + prefix + body + suffix + text.slice(e);
    const newStart = s + prefix.length;
    const newEnd = newStart + body.length;
    return { value, selectionStart: newStart, selectionEnd: newEnd };
  };
}

function linePrefix(prefix: string): MdTransform {
  return (text, s, e) => {
    // Acha início da linha onde a seleção começa.
    let lineStart = s;
    while (lineStart > 0 && text[lineStart - 1] !== "\n") lineStart--;
    let lineEnd = e;
    while (lineEnd < text.length && text[lineEnd] !== "\n") lineEnd++;
    const block = text.slice(lineStart, lineEnd);
    const transformed = block
      .split("\n")
      .map((ln) => (ln.startsWith(prefix) ? ln : `${prefix}${ln}`))
      .join("\n");
    const value = text.slice(0, lineStart) + transformed + text.slice(lineEnd);
    const newStart = lineStart;
    const newEnd = lineStart + transformed.length;
    return { value, selectionStart: newStart, selectionEnd: newEnd };
  };
}

/** Insere um bloco no início da próxima linha (ou na linha atual se vazia). */
function insertBlock(block: string, placeholder = ""): MdTransform {
  return (text, s, _e) => {
    // Acha início da linha onde o cursor está.
    let lineStart = s;
    while (lineStart > 0 && text[lineStart - 1] !== "\n") lineStart--;
    let lineEnd = s;
    while (lineEnd < text.length && text[lineEnd] !== "\n") lineEnd++;
    const lineContent = text.slice(lineStart, lineEnd);
    const needsLeadingBreak = lineContent.trim().length > 0;
    const prefix = needsLeadingBreak ? "\n\n" : "";
    const suffix = "\n";
    const body = block + (placeholder ? "" : "");
    const insertPos = needsLeadingBreak ? lineEnd : lineStart;
    const value =
      text.slice(0, insertPos) + prefix + body + suffix + text.slice(insertPos);
    const sel = insertPos + prefix.length;
    return {
      value,
      selectionStart: sel,
      selectionEnd: sel + body.length
    };
  };
}

function buildTable(rows: number, cols: number): string {
  const header = "| " + Array.from({ length: cols }, (_, i) => `Col ${i + 1}`).join(" | ") + " |";
  const sep = "| " + Array.from({ length: cols }, () => "---").join(" | ") + " |";
  const body = Array.from(
    { length: rows },
    () => "| " + Array.from({ length: cols }, () => " ").join(" | ") + " |"
  ).join("\n");
  return `${header}\n${sep}\n${body}`;
}

/** Hook: textarea aceita paste/drop de imagem → insere `![alt](dataUrl)`
 *  na posição do cursor. */
function useImagePasteOnTextarea(
  ref: { current: HTMLTextAreaElement | null },
  insertText: (md: string) => void
) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    async function handleFile(file: File) {
      try {
        const dataUrl = await fileToDataUrl(file);
        const alt = file.name.replace(/\.[^.]+$/, "") || "imagem";
        insertText(`\n\n![${alt}](${dataUrl})\n\n`);
      } catch {
        /* ignore */
      }
    }
    function onPaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === "file" && it.type.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            void handleFile(f);
            return;
          }
        }
      }
    }
    function onDrop(e: DragEvent) {
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      for (const f of Array.from(files)) {
        if (f.type.startsWith("image/")) {
          e.preventDefault();
          void handleFile(f);
          return;
        }
      }
    }
    function onDragOver(e: DragEvent) {
      if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
    }
    el.addEventListener("paste", onPaste);
    el.addEventListener("drop", onDrop);
    el.addEventListener("dragover", onDragOver);
    return () => {
      el.removeEventListener("paste", onPaste);
      el.removeEventListener("drop", onDrop);
      el.removeEventListener("dragover", onDragOver);
    };
  }, [ref, insertText]);
}

async function fileToDataUrl(file: File): Promise<string> {
  // Redimensiona pra ~1280px no maior lado e gera data URL (jpeg/png/webp).
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("img_load_failed"));
      el.src = url;
    });
    const MAX = 1280;
    let { width, height } = img;
    if (width > MAX || height > MAX) {
      const ratio = Math.min(MAX / width, MAX / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no_canvas_ctx");
    ctx.drawImage(img, 0, 0, width, height);
    // Mantém formato com transparência se vier PNG; senão WebP pra economizar.
    const mime = file.type === "image/png" || file.type === "image/gif" ? "image/png" : "image/webp";
    return canvas.toDataURL(mime, 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function FormatToolbar({ onApply }: { onApply: (t: MdTransform) => void }) {
  const [tablePickerOpen, setTablePickerOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const groups: Array<{
    key: string;
    items: Array<{
      key: string;
      icon: typeof Bold;
      title: string;
      onClick: () => void;
      color?: string;
    }>;
  }> = [
    {
      key: "format",
      items: [
        { key: "b", icon: Bold, title: "Negrito (**)", onClick: () => onApply(wrapSelection("**", "**", "negrito")) },
        { key: "i", icon: Italic, title: "Itálico (_)", onClick: () => onApply(wrapSelection("_", "_", "itálico")) },
        { key: "u", icon: Underline, title: "Sublinhado (++)", onClick: () => onApply(wrapSelection("++", "++", "sublinhado")) },
        { key: "s", icon: Strikethrough, title: "Tachado (~~)", onClick: () => onApply(wrapSelection("~~", "~~", "tachado")) },
        { key: "mark", icon: Highlighter, title: "Marca-texto (==)", onClick: () => onApply(wrapSelection("==", "==", "destaque")), color: "text-amber-600 dark:text-amber-300" },
        { key: "code", icon: Code, title: "Código inline (`)", onClick: () => onApply(wrapSelection("`", "`", "code")) }
      ]
    },
    {
      key: "headings",
      items: [
        { key: "h1", icon: Heading1, title: "Título 1", onClick: () => onApply(linePrefix("# ")) },
        { key: "h2", icon: Heading2, title: "Título 2", onClick: () => onApply(linePrefix("## ")) },
        { key: "h3", icon: Heading3, title: "Título 3", onClick: () => onApply(linePrefix("### ")) }
      ]
    },
    {
      key: "blocks",
      items: [
        { key: "ul", icon: List, title: "Lista", onClick: () => onApply(linePrefix("- ")) },
        { key: "ol", icon: ListOrdered, title: "Lista numerada", onClick: () => onApply(linePrefix("1. ")) },
        { key: "quote", icon: Quote, title: "Citação", onClick: () => onApply(linePrefix("> ")) },
        { key: "codeblock", icon: Code2, title: "Bloco de código", onClick: () => onApply(insertBlock("```\ncódigo aqui\n```")) },
        { key: "hr", icon: Minus, title: "Régua horizontal", onClick: () => onApply(insertBlock("---")) }
      ]
    },
    {
      key: "align",
      items: [
        { key: "al", icon: AlignLeft, title: "Alinhar à esquerda", onClick: () => onApply(insertBlock("::: left\nseu texto\n:::")) },
        { key: "ac", icon: AlignCenter, title: "Centralizar", onClick: () => onApply(insertBlock("::: center\nseu texto\n:::")) },
        { key: "ar", icon: AlignRight, title: "Alinhar à direita", onClick: () => onApply(insertBlock("::: right\nseu texto\n:::")) }
      ]
    },
    {
      key: "insert",
      items: [
        { key: "link", icon: Link2, title: "Link", onClick: () => onApply(wrapSelection("[", "](https://)", "texto")) },
        { key: "image", icon: ImageIcon, title: "Imagem (URL ou enviar)", onClick: () => setImageDialogOpen(true) },
        { key: "table", icon: TableIcon, title: "Inserir tabela", onClick: () => setTablePickerOpen(true) }
      ]
    }
  ];

  async function handleUploadFile(file: File) {
    try {
      const dataUrl = await fileToDataUrl(file);
      const alt = file.name.replace(/\.[^.]+$/, "");
      onApply(insertBlock(`![${alt}](${dataUrl})`));
    } catch {
      // ignore — fallback simples
    } finally {
      setImageDialogOpen(false);
    }
  }

  return (
    <>
      <div class="flex shrink-0 flex-wrap items-center gap-0.5 border-b border-border bg-card/30 px-1.5 py-1">
        {groups.map((g, gi) => (
          <div key={g.key} class="flex items-center gap-0.5">
            {gi > 0 && <div class="mx-1 h-5 w-px bg-border/60" />}
            {g.items.map((b) => {
              const Icon = b.icon;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={b.onClick}
                  title={b.title}
                  aria-label={b.title}
                  class={`grid size-7 place-items-center rounded hover:bg-accent hover:text-foreground ${
                    b.color ?? "text-muted-foreground"
                  }`}
                >
                  <Icon className="size-3.5" />
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {tablePickerOpen && (
        <TablePickerDialog
          onCancel={() => setTablePickerOpen(false)}
          onConfirm={(rows, cols) => {
            onApply(insertBlock(buildTable(rows, cols)));
            setTablePickerOpen(false);
          }}
        />
      )}

      {imageDialogOpen && (
        <ImageInsertDialog
          onCancel={() => setImageDialogOpen(false)}
          onSubmitUrl={(url, alt) => {
            onApply(insertBlock(`![${alt || "imagem"}](${url})`));
            setImageDialogOpen(false);
          }}
          onPickFile={() => fileRef.current?.click()}
        />
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        aria-label="Selecionar imagem do dispositivo"
        class="hidden"
        onChange={(e) => {
          const f = (e.target as HTMLInputElement).files?.[0];
          if (f) void handleUploadFile(f);
          (e.target as HTMLInputElement).value = "";
        }}
      />
    </>
  );
}

function TablePickerDialog({
  onCancel,
  onConfirm
}: {
  onCancel: () => void;
  onConfirm: (rows: number, cols: number) => void;
}) {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  return (
    <div
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        class="w-full max-w-xs rounded-lg border border-border bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 class="mb-3 text-sm font-bold">Inserir tabela</h3>
        <div class="space-y-2 text-xs">
          <label class="flex items-center justify-between gap-2">
            <span>Linhas</span>
            <input
              type="number"
              min={1}
              max={20}
              value={rows}
              onInput={(e) => setRows(Math.max(1, Math.min(20, Number((e.target as HTMLInputElement).value) || 1)))}
              class="w-20 rounded border border-border bg-background px-2 py-1 text-right"
            />
          </label>
          <label class="flex items-center justify-between gap-2">
            <span>Colunas</span>
            <input
              type="number"
              min={1}
              max={10}
              value={cols}
              onInput={(e) => setCols(Math.max(1, Math.min(10, Number((e.target as HTMLInputElement).value) || 1)))}
              class="w-20 rounded border border-border bg-background px-2 py-1 text-right"
            />
          </label>
        </div>
        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(rows, cols)}
            class="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
          >
            Inserir
          </button>
        </div>
      </div>
    </div>
  );
}

function ImageInsertDialog({
  onCancel,
  onSubmitUrl,
  onPickFile
}: {
  onCancel: () => void;
  onSubmitUrl: (url: string, alt: string) => void;
  onPickFile: () => void;
}) {
  const [url, setUrl] = useState("");
  const [alt, setAlt] = useState("");
  return (
    <div
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        class="w-full max-w-md rounded-lg border border-border bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 class="mb-3 text-sm font-bold">Inserir imagem</h3>
        <button
          type="button"
          onClick={onPickFile}
          class="mb-3 flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/30 px-3 py-4 text-xs font-medium hover:bg-accent"
        >
          <Upload className="size-4" />
          Enviar do computador
        </button>
        <div class="mb-2 text-center text-[10px] uppercase tracking-wider text-muted-foreground">
          ou cole uma URL
        </div>
        <div class="space-y-2 text-xs">
          <input
            type="url"
            value={url}
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
            placeholder="https://…"
            class="w-full rounded border border-border bg-background px-2 py-1.5 outline-none focus:border-primary"
          />
          <input
            type="text"
            value={alt}
            onInput={(e) => setAlt((e.target as HTMLInputElement).value)}
            placeholder="Descrição (alt) — opcional"
            class="w-full rounded border border-border bg-background px-2 py-1.5 outline-none focus:border-primary"
          />
        </div>
        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!url.trim()}
            onClick={() => onSubmitUrl(url.trim(), alt.trim())}
            class="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40"
          >
            Inserir
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// FocusNoteModal — abre a anotação em tela cheia pra escrita focada. Mesma
// toolbar de formatação, sem distrações.
// ============================================================================

function FocusNoteModal({
  title,
  contentMd,
  onChangeTitle,
  onChangeContent,
  onClose
}: {
  title: string;
  contentMd: string;
  onChangeTitle: (v: string) => void;
  onChangeContent: (v: string) => void;
  onClose: () => void;
}) {
  // ESC fecha.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div class="fixed inset-0 z-50 flex flex-col bg-background">
      <header class="flex shrink-0 items-center gap-2 border-b border-border bg-card/40 px-4 py-3">
        <input
          type="text"
          value={title}
          onInput={(e) => onChangeTitle((e.target as HTMLInputElement).value)}
          placeholder="Título…"
          maxLength={200}
          aria-label="Título"
          class="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-base font-bold outline-none focus:border-primary"
        />
        <button
          type="button"
          onClick={onClose}
          title="Sair do foco (Esc)"
          aria-label="Sair do foco"
          class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Minimize2 className="size-4" />
        </button>
      </header>
      {/* RichTextEditor é WYSIWYG — mesma toolbar + atalhos do editor inline,
          ocupando a tela toda. Margens generosas pra escrita focada. */}
      <div class="flex min-h-0 flex-1 flex-col px-8 md:px-24">
        <RichTextEditor
          initialValue={contentMd}
          onChange={onChangeContent}
          placeholder="Escreva sua anotação… Auto-save."
        />
      </div>
    </div>
  );
}

// =====================================================================
// RichTextEditor — WYSIWYG simples baseado em contentEditable +
// document.execCommand. Trocado pelo textarea-markdown em 2026-05-20
// porque o usuário-final é leigo (acostumado com Word, não com sintaxe
// markdown). [[project-notes-wysiwyg]]
//
// Strategy:
//   - Detecta se `initialValue` é markdown ou já HTML; se markdown,
//     converte uma vez via renderMdBlock antes de injetar no DOM.
//   - ContentEditable não-controlado (innerHTML setado imperativamente
//     no mount + on note change via key prop).
//   - onChange emite innerHTML cru após cada input. Auto-save do parent
//     debounce o storage.
//   - Toolbar usa execCommand (deprecated mas universalmente suportado
//     em todos os browsers atuais). Migrar pra Tiptap/Slate é overhead
//     incompatível com a urgência atual — fica no backlog.
// =====================================================================

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/** Converte o conteúdo salvo de uma nota (HTML novo ou markdown legado) em
 *  HTML pronto pra renderizar com dangerouslySetInnerHTML. Usado no hub,
 *  modal de leitura, busca etc. Sempre aparece WYSIWYG, nunca como código. */
export function noteContentToHtml(content: string): string {
  const raw = (content ?? "").trim();
  if (!raw) return "";
  if (looksLikeHtml(raw)) return raw;
  try {
    return renderMdBlock(raw);
  } catch {
    return `<p>${escapeHtml(raw)}</p>`;
  }
}

/** Converte conteúdo da nota em texto puro (sem tags HTML, sem marcadores
 *  markdown) — pra preview de card e indexação de busca. Não usa DOMParser
 *  pra evitar criação de árvores DOM caras em listas grandes. */
export function noteContentToPreview(content: string, max = 220): string {
  const raw = (content ?? "").trim();
  if (!raw) return "";
  let plain = raw;
  if (looksLikeHtml(plain)) {
    // Remove tags + decoda entidades comuns. Lookahead curto preserva quebras
    // em parágrafos consecutivos como espaço.
    plain = plain
      .replace(/<\/(p|div|li|h[1-6]|tr|br)>/gi, " ")
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  } else {
    // Markdown legado — remove marcadores comuns.
    plain = plain
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/^[-*+]\s+/gm, "")
      .replace(/^>\s+/gm, "");
  }
  plain = plain.replace(/\s+/g, " ").trim();
  return plain.length > max ? `${plain.slice(0, max).trimEnd()}…` : plain;
}

function looksLikeHtml(s: string): boolean {
  // Heurística: existe pelo menos uma tag HTML common? Strings que começam
  // com `<p`, `<div`, `<strong`, etc.
  return /<(p|div|span|strong|em|u|s|h[1-6]|ul|ol|li|blockquote|a|br|hr|img|table|tr|td|th|code|pre)[\s>/]/i.test(s);
}

// Paletas pequenas — caem em luz natural e em dark, sem precisar tematizar.
// Cores chapadas (#hex) porque execCommand resolve via styleWithCSS pra
// `<span style="color: …">` que persiste no HTML salvo.
const TEXT_COLOR_PALETTE: { label: string; value: string }[] = [
  { label: "Padrão", value: "" },
  { label: "Vermelho", value: "#dc2626" },
  { label: "Laranja", value: "#ea580c" },
  { label: "Âmbar", value: "#ca8a04" },
  { label: "Verde", value: "#16a34a" },
  { label: "Azul", value: "#2563eb" },
  { label: "Violeta", value: "#7c3aed" },
  { label: "Rosa", value: "#db2777" }
];

const HIGHLIGHT_PALETTE: { label: string; value: string }[] = [
  { label: "Nenhum", value: "" },
  { label: "Amarelo", value: "#fef08a" },
  { label: "Verde", value: "#bbf7d0" },
  { label: "Azul", value: "#bfdbfe" },
  { label: "Roxo", value: "#e9d5ff" },
  { label: "Rosa", value: "#fbcfe8" },
  { label: "Laranja", value: "#fed7aa" }
];

type CalloutVariant = "info" | "tip" | "warn";
const CALLOUT_PRESETS: { id: CalloutVariant; label: string; icon: string }[] = [
  { id: "info", label: "Informação", icon: "📘" },
  { id: "tip",  label: "Dica",       icon: "💡" },
  { id: "warn", label: "Aviso",      icon: "⚠️" }
];

interface ActiveFormats {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  ul: boolean;
  ol: boolean;
  h1: boolean;
  h2: boolean;
  h3: boolean;
  blockquote: boolean;
  foreColor: string;
  backColor: string;
}

const EMPTY_ACTIVE: ActiveFormats = {
  bold: false, italic: false, underline: false, strike: false,
  ul: false, ol: false, h1: false, h2: false, h3: false, blockquote: false,
  foreColor: "", backColor: ""
};

function RichTextEditor({
  initialValue,
  onChange,
  placeholder
}: {
  initialValue: string;
  onChange: (html: string) => void;
  placeholder?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const [active, setActive] = useState<ActiveFormats>(EMPTY_ACTIVE);
  const [openMenu, setOpenMenu] = useState<null | "color" | "highlight" | "callout" | "template">(null);

  // Inicializa innerHTML uma vez no mount (e quando muda o `key` do parent).
  useEffect(() => {
    if (!ref.current) return;
    // styleWithCSS=true faz execCommand emitir `<span style="...">` em vez
    // de `<font color=...>` (legacy). Persiste melhor no save + dark mode.
    try { document.execCommand("styleWithCSS", false, "true"); } catch { /* ignore */ }
    let initialHtml = initialValue ?? "";
    if (initialHtml && !looksLikeHtml(initialHtml)) {
      try {
        initialHtml = renderMdBlock(initialHtml);
      } catch {
        initialHtml = `<p>${initialHtml.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`;
      }
    }
    // Nota vazia precisa de pelo menos um <p><br></p> pra que `formatBlock`,
    // `createLink` e outros execCommand tenham um bloco pai pra operar.
    // Sem isso, o aluno abre nota nova, digita texto, clica H1 → no-op porque
    // o browser não acha block ancestor pra converter. [[project-notes-wysiwyg]]
    ref.current.innerHTML = initialHtml || "<p><br></p>";
    setIsEmpty(ref.current.textContent?.trim().length === 0);
    // Renderiza fórmulas LaTeX existentes (notas carregadas com `\(...\)`).
    if (initialHtml.includes("math-inline") || /\\\(|\\\[/.test(initialHtml)) {
      void ensureKaTeX().then(() => {
        if (ref.current) renderMathIn(ref.current);
      });
    }
  }, []);

  // Atualiza estado ativo da toolbar quando a seleção muda (foco no editor).
  useEffect(() => {
    function refresh() {
      const sel = window.getSelection();
      if (!sel || !sel.anchorNode || !ref.current) return;
      if (!ref.current.contains(sel.anchorNode)) return;
      let block = "";
      try { block = (document.queryCommandValue("formatBlock") || "").toUpperCase(); } catch { /* ignore */ }
      let fore = "";
      let back = "";
      try { fore = String(document.queryCommandValue("foreColor") || ""); } catch { /* ignore */ }
      try { back = String(document.queryCommandValue("backColor") || document.queryCommandValue("hiliteColor") || ""); } catch { /* ignore */ }
      setActive({
        bold:       safeQueryState("bold"),
        italic:     safeQueryState("italic"),
        underline:  safeQueryState("underline"),
        strike:     safeQueryState("strikeThrough"),
        ul:         safeQueryState("insertUnorderedList"),
        ol:         safeQueryState("insertOrderedList"),
        h1:         block === "H1",
        h2:         block === "H2",
        h3:         block === "H3",
        blockquote: block === "BLOCKQUOTE",
        foreColor:  fore,
        backColor:  back
      });
    }
    document.addEventListener("selectionchange", refresh);
    return () => document.removeEventListener("selectionchange", refresh);
  }, []);

  // Click fora fecha dropdown.
  useEffect(() => {
    if (!openMenu) return;
    function onDown(e: MouseEvent) {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-rt-menu]")) setOpenMenu(null);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openMenu]);

  // ----- Pacote 3: bolha de IA flutuante sobre a seleção ------------
  // Quando o aluno seleciona um trecho, aparece uma bolha acima da seleção
  // com botões "Explique", "Resumir", "Melhorar". Resultado vira um callout
  // logo abaixo do trecho (não substitui — aditivo, seguro). [[project-notes-system-caderno]]
  const [aiBubble, setAiBubble] = useState<{ x: number; y: number; text: string } | null>(null);
  const [aiBusy, setAiBusy] = useState<null | "explain" | "summarize" | "improve" | "flashcards">(null);

  // Popover de link — abre quando aluno clica em <a> dentro do editor.
  // Mostra URL + Abrir / Editar / Remover. Em contentEditable o click no
  // <a> não navega nativamente (entra modo edição), então precisamos disso.
  const [linkPopover, setLinkPopover] = useState<{
    x: number;
    y: number;
    href: string;
    anchorEl: HTMLAnchorElement;
  } | null>(null);

  // Click fora do popover do link fecha. Reativo a linkPopover.
  useEffect(() => {
    if (!linkPopover) return;
    function onDown(e: MouseEvent) {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest("[data-rt-link-popover]")) return; // click DENTRO do popover
      if (t === linkPopover?.anchorEl || linkPopover?.anchorEl.contains(t)) return; // re-click no link
      setLinkPopover(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setLinkPopover(null);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [linkPopover]);

  async function editLinkHref(anchor: HTMLAnchorElement) {
    setLinkPopover(null);
    const current = anchor.getAttribute("href") || "";
    const next = await openPrompt({
      title: "Editar link",
      message: "Atualize a URL. Apague tudo para remover o link mantendo o texto.",
      defaultValue: current,
      placeholder: "https://…",
      confirmLabel: "Salvar"
    });
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed) {
      // Vazio = remover link, manter texto
      const parent = anchor.parentNode;
      while (anchor.firstChild) parent?.insertBefore(anchor.firstChild, anchor);
      anchor.remove();
      emitChange();
      return;
    }
    const normalized = /^([a-z]+:|\/\/|mailto:|tel:)/i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
    anchor.setAttribute("href", normalized);
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener");
    emitChange();
  }

  function removeLink(anchor: HTMLAnchorElement) {
    setLinkPopover(null);
    const parent = anchor.parentNode;
    while (anchor.firstChild) parent?.insertBefore(anchor.firstChild, anchor);
    anchor.remove();
    emitChange();
  }

  useEffect(() => {
    function updateBubble() {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.anchorNode || !ref.current) {
        setAiBubble(null);
        return;
      }
      if (!ref.current.contains(sel.anchorNode)) {
        setAiBubble(null);
        return;
      }
      // Bolha de IA gated por feature flag (admin habilita no manager-new).
      // Default OFF — custo de IA + decisão comercial. [[project-notes-ai-flags]]
      if (!flagEnabled("student.notes.inline_ai", false)) {
        setAiBubble(null);
        return;
      }
      const text = sel.toString().trim();
      if (text.length < 8) {
        // muito curto pra valer a pena chamar IA
        setAiBubble(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setAiBubble(null);
        return;
      }
      // Bolha logo acima do início da seleção, com clamp horizontal pra não
      // sair do viewport.
      const PADDING = 8;
      const x = Math.max(PADDING, Math.min(window.innerWidth - 280, rect.left));
      const y = Math.max(PADDING, rect.top - 44);
      setAiBubble({ x, y, text });
    }
    document.addEventListener("selectionchange", updateBubble);
    window.addEventListener("scroll", updateBubble, true);
    window.addEventListener("resize", updateBubble);
    return () => {
      document.removeEventListener("selectionchange", updateBubble);
      window.removeEventListener("scroll", updateBubble, true);
      window.removeEventListener("resize", updateBubble);
    };
  }, []);

  async function runAiAction(action: "explain" | "summarize" | "improve" | "flashcards") {
    if (!aiBubble || aiBusy) return;
    const selectedText = aiBubble.text;
    setAiBusy(action);
    // Salva o range pra reinserir o resultado no ponto certo mesmo que
    // a seleção mude durante a chamada de rede.
    const sel = window.getSelection();
    const savedRange = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    try {
      const resp = await api.aiQuickText({ action, text: selectedText });
      const replyHtml = formatAiReply(action, resp.text);
      insertAiCalloutAfter(savedRange, replyHtml);
      setAiBubble(null);
    } catch (e) {
      const msg = friendlyError(e);
      await openAlert({
        title: "Não consegui processar",
        message: msg,
        confirmLabel: "Ok"
      });
    } finally {
      setAiBusy(null);
    }
  }

  function formatAiReply(action: "explain" | "summarize" | "improve" | "flashcards", text: string): string {
    const ICONS: Record<typeof action, string> = {
      explain: "📘",
      summarize: "📝",
      improve: "✨",
      flashcards: "🎴"
    } as const;
    const LABELS: Record<typeof action, string> = {
      explain: "Explicação",
      summarize: "Resumo",
      improve: "Versão melhorada",
      flashcards: "Flashcards"
    } as const;
    const variant = action === "improve" ? "tip" : action === "flashcards" ? "warn" : "info";
    const paragraphs = text
      .split(/\n{2,}/)
      .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
      .join("");
    return `<aside class="callout callout-${variant}" data-callout="${variant}" data-ai-action="${action}"><span class="callout-icon" contenteditable="false">${ICONS[action]}</span><div class="callout-body"><p><strong>${LABELS[action]}</strong></p>${paragraphs}</div></aside><p><br></p>`;
  }

  function insertAiCalloutAfter(savedRange: Range | null, html: string) {
    if (!ref.current) return;
    ref.current.focus();
    if (savedRange) {
      // Restaura seleção e move o caret pro FIM dela antes de inserir.
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        const collapseEnd = savedRange.cloneRange();
        collapseEnd.collapse(false);
        sel.addRange(collapseEnd);
      }
    }
    try {
      document.execCommand("styleWithCSS", false, "true");
      document.execCommand("insertHTML", false, html);
    } catch { /* ignore */ }
    emitChange();
  }
  // ----- fim pacote 3 ------------------------------------------------

  function safeQueryState(cmd: string): boolean {
    try { return document.queryCommandState(cmd); } catch { return false; }
  }

  // Salva/restaura a seleção do editor pra sobreviver a operações async
  // (abrir modal de prompt, fetch, etc). Sem isso, openPrompt/window.prompt
  // tiram o foco do contentEditable e o caret some — execCommand depois
  // não tem onde inserir e a operação vira no-op.
  function saveSelection(): Range | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !ref.current) return null;
    const range = sel.getRangeAt(0);
    if (!ref.current.contains(range.commonAncestorContainer)) return null;
    return range.cloneRange();
  }

  function restoreSelection(range: Range | null) {
    if (!ref.current) return;
    ref.current.focus();
    if (!range) return;
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);
  }

  function exec(command: string, value?: string) {
    if (!ref.current) return;
    ref.current.focus();
    try { document.execCommand("styleWithCSS", false, "true"); } catch { /* ignore */ }
    try { document.execCommand(command, false, value); } catch { /* ignore */ }
    emitChange();
  }

  function execFormat(block: string) {
    // formatBlock cross-browser quer o valor no formato `<h1>` (lowercase
    // com angle brackets). Sem isso, Firefox e algumas versões do Chrome
    // tratam como no-op silencioso. [[project-notes-wysiwyg]]
    const tag = block.toLowerCase().replace(/[<>]/g, "");
    exec("formatBlock", `<${tag}>`);
  }

  // Toggle: se o bloco atual JÁ É o solicitado, desfaz pra parágrafo.
  // Comportamento esperado por usuários de Word/Docs (clique no H1 ativo
  // desfaz pra texto normal). Usado por H1/H2/H3/blockquote.
  function execFormatToggle(block: string) {
    let current = "";
    try {
      current = (document.queryCommandValue("formatBlock") || "").toUpperCase();
    } catch { /* ignore */ }
    if (current === block.toUpperCase()) {
      execFormat("p");
    } else {
      execFormat(block);
    }
  }

  function emitChange() {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    onChange(html);
    setIsEmpty(ref.current.textContent?.trim().length === 0);
  }

  async function insertLink() {
    // openPrompt é modal — rouba o foco do contentEditable. Salvamos a
    // seleção antes e restauramos antes de executar createLink (que opera
    // em cima da seleção atual).
    const saved = saveSelection();
    const url = await openPrompt({
      title: "Inserir link",
      message: "Endereço (URL). Se você tinha texto selecionado, ele vira o link clicável; senão a URL aparece como texto.",
      placeholder: "https://…",
      confirmLabel: "Inserir"
    });
    if (!url || !url.trim()) return;
    if (!ref.current) return;

    // Restaura foco + seleção INLINE (sem usar exec/restoreSelection
    // separados, pra evitar dupla chamada de focus que reseta o caret em
    // alguns browsers).
    ref.current.focus();
    if (saved) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(saved);
      }
    }

    // Normaliza URL — se aluno digitou sem protocolo, prefixa https://.
    let finalUrl = url.trim();
    if (!/^([a-z]+:|\/\/|mailto:|tel:)/i.test(finalUrl)) {
      finalUrl = `https://${finalUrl}`;
    }

    const sel = window.getSelection();
    const collapsed = !sel || sel.isCollapsed;

    try { document.execCommand("styleWithCSS", false, "true"); } catch { /* ignore */ }
    if (collapsed) {
      // Sem seleção: insere `<a>URL</a>` com o próprio URL visível como texto.
      const safe = escapeAttr(finalUrl);
      document.execCommand(
        "insertHTML",
        false,
        `<a href="${safe}" target="_blank" rel="noopener">${escapeHtml(finalUrl)}</a>`
      );
    } else {
      // Com seleção: createLink wrappa o texto no <a>. Em seguida, garantimos
      // target=_blank + rel=noopener no <a> recém-criado (createLink não permite).
      document.execCommand("createLink", false, finalUrl);
      try {
        const sel2 = window.getSelection();
        if (sel2 && sel2.anchorNode) {
          const el = sel2.anchorNode instanceof Element
            ? sel2.anchorNode
            : sel2.anchorNode.parentElement;
          const a = el?.closest("a[href]");
          if (a) {
            a.setAttribute("target", "_blank");
            a.setAttribute("rel", "noopener");
          }
        }
      } catch { /* ignore */ }
    }
    emitChange();
  }

  function applyColor(value: string) {
    setOpenMenu(null);
    if (!value) {
      // Resetar cor — execCommand com "inherit" remove o style inline.
      exec("foreColor", "inherit");
      return;
    }
    exec("foreColor", value);
  }

  function applyHighlight(value: string) {
    setOpenMenu(null);
    if (!value) {
      exec("hiliteColor", "transparent");
      return;
    }
    // Firefox usa "hiliteColor", Chrome/Edge também aceitam. Fallback: backColor.
    try {
      document.execCommand("styleWithCSS", false, "true");
      const ok = document.execCommand("hiliteColor", false, value);
      if (!ok) document.execCommand("backColor", false, value);
    } catch { /* ignore */ }
    emitChange();
  }

  // Converte a linha atual (ou múltiplas, se houver seleção) em task-list.
  // Estratégia: usar `insertUnorderedList` (que já faz o trabalho pesado de
  // achar o bloco, reusar o texto, lidar com multi-linha) e DEPOIS decorar a
  // UL com class="task-list" + injetar o <span class="task-check"> em cada <li>.
  // Toggle: se a linha já está em task-list, sai dela (mesma chamada toggla a UL).
  function insertChecklist() {
    if (!ref.current) return;
    ref.current.focus();

    // Detecta se o caret está dentro de uma task-list existente.
    const sel = window.getSelection();
    const anchor = sel?.anchorNode;
    const insideTaskList = (() => {
      const el = anchor instanceof Element ? anchor : anchor?.parentElement ?? null;
      return !!el?.closest("ul.task-list");
    })();

    // execCommand("insertUnorderedList") já é toggle nativo. Se está em UL
    // (task-list ou plain), sai. Se não está, entra em UL plain.
    try { document.execCommand("insertUnorderedList"); } catch { /* ignore */ }

    if (insideTaskList) {
      // Estava em task-list → execCommand removeu UL. Nada mais a fazer.
      emitChange();
      return;
    }

    // Não estava em lista → execCommand criou UL plain. Decora todas as
    // <li> da UL recém-criada com o checkbox e marca a UL como task-list.
    const sel2 = window.getSelection();
    const anchor2 = sel2?.anchorNode;
    const li = (anchor2 instanceof Element ? anchor2 : anchor2?.parentElement ?? null)?.closest("li");
    const ul = li?.parentElement;
    if (ul && ul.tagName === "UL") {
      ul.classList.add("task-list");
      ul.querySelectorAll("li").forEach((item) => {
        if (item.querySelector(":scope > .task-check")) return; // já tem
        item.setAttribute("data-checked", "false");
        const span = document.createElement("span");
        span.className = "task-check";
        span.setAttribute("data-checked", "false");
        span.setAttribute("contenteditable", "false");
        span.setAttribute("role", "checkbox");
        span.setAttribute("aria-checked", "false");
        span.setAttribute("aria-label", "Marcar item");
        span.textContent = "☐";
        // Insere o span ANTES de tudo na li + um espaço pra separar do texto.
        item.insertBefore(document.createTextNode(" "), item.firstChild);
        item.insertBefore(span, item.firstChild);
      });
    }
    emitChange();
  }

  function insertCallout(variant: CalloutVariant) {
    setOpenMenu(null);
    const icon = CALLOUT_PRESETS.find((c) => c.id === variant)?.icon ?? "📘";
    const html = `<aside class="callout callout-${variant}" data-callout="${variant}"><span class="callout-icon" contenteditable="false">${icon}</span><div class="callout-body"><p>Texto da caixa…</p></div></aside><p><br></p>`;
    exec("insertHTML", html);
  }

  function insertHr() {
    exec("insertHorizontalRule");
  }

  // Limpa TUDO da seleção: inline (negrito/itálico/cor/etc), link, e bloco
  // (heading/blockquote vira parágrafo, lista some). Pede confirmação com
  // "LIMPAR" — mesmo padrão do APAGAR — porque a operação é destrutiva e
  // não dá pra desfazer com Ctrl+Z em todos os browsers consistentemente.
  async function clearFormatting() {
    // Salva a seleção ANTES do modal (modal rouba foco e seleção).
    const saved = saveSelection();
    const confirmed = await openPrompt({
      title: "Limpar formatação?",
      message:
        "Isso vai remover do trecho selecionado: negrito, itálico, cor, marca-texto, link, título, citação e lista. O texto continua, só sem formatação. Digite LIMPAR para confirmar.",
      requirePhrase: "LIMPAR",
      confirmLabel: "Limpar",
      danger: true
    });
    if (confirmed == null) return;
    if (!ref.current) return;
    // Restaura foco + seleção antes de executar os comandos.
    ref.current.focus();
    if (saved) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(saved);
      }
    }
    // 1) Inline (negrito, itálico, sublinhado, tachado, cor, marca-texto)
    exec("removeFormat");
    // 2) Link
    exec("unlink");
    // 3) Bloco — H1/H2/H3/blockquote → <p>
    execFormat("p");
    // 4) Se está dentro de lista, sai dela (execCommand toggla)
    try {
      if (document.queryCommandState("insertUnorderedList")) {
        exec("insertUnorderedList");
      }
      if (document.queryCommandState("insertOrderedList")) {
        exec("insertOrderedList");
      }
    } catch { /* ignore */ }
  }

  // Insere data/hora atual formatado em PT-BR no caret.
  function insertDateTime() {
    const now = new Date();
    const formatted = now.toLocaleString("pt-BR", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
    exec("insertHTML", `<span class="text-muted-foreground">${escapeHtml(formatted)}</span>&nbsp;`);
  }

  // Templates pré-prontos pra ajudar o aluno a começar. Insere no caret
  // (no início do editor se vazio, ou onde estiver). [[project-notes-system-caderno]]
  const NOTE_TEMPLATES: { id: string; label: string; emoji: string; html: () => string }[] = [
    {
      id: "aula",
      label: "Resumo de aula",
      emoji: "📚",
      html: () =>
        `<h2>📚 Resumo da aula — ${escapeHtml(new Date().toLocaleDateString("pt-BR"))}</h2><p><strong>Tema:</strong> </p><p><strong>Pontos principais:</strong></p><ul><li> </li><li> </li><li> </li></ul><p><strong>Dúvidas pra revisar:</strong></p><ul><li> </li></ul><p><strong>Tarefas:</strong></p><ul class="task-list"><li data-checked="false"><span class="task-check" data-checked="false" contenteditable="false" role="checkbox" aria-checked="false">☐</span>&nbsp;</li></ul><p><br></p>`
    },
    {
      id: "leitura",
      label: "Notas de leitura",
      emoji: "📖",
      html: () =>
        `<h2>📖 Notas de leitura</h2><p><strong>Fonte:</strong> </p><p><strong>Páginas / Capítulo:</strong> </p><p><strong>Ideias-chave:</strong></p><ul><li> </li><li> </li></ul><blockquote>Citação relevante</blockquote><p><strong>Meus comentários:</strong></p><p> </p>`
    },
    {
      id: "questao",
      label: "Análise de questão",
      emoji: "🧠",
      html: () =>
        `<h2>🧠 Análise da questão</h2><p><strong>Enunciado:</strong> </p><p><strong>Alternativas:</strong></p><ol><li>A. </li><li>B. </li><li>C. </li><li>D. </li><li>E. </li></ol><p><strong>Gabarito:</strong> </p><p><strong>Por que essa é a resposta:</strong></p><p> </p><p><strong>Por que as outras estão erradas:</strong></p><ul><li> </li></ul>`
    },
    {
      id: "reuniao",
      label: "Pauta / Reunião",
      emoji: "📋",
      html: () =>
        `<h2>📋 Reunião — ${escapeHtml(new Date().toLocaleDateString("pt-BR"))}</h2><p><strong>Participantes:</strong> </p><p><strong>Pauta:</strong></p><ol><li> </li><li> </li></ol><p><strong>Decisões:</strong></p><ul><li> </li></ul><p><strong>Próximos passos:</strong></p><ul class="task-list"><li data-checked="false"><span class="task-check" data-checked="false" contenteditable="false" role="checkbox" aria-checked="false">☐</span>&nbsp;</li></ul>`
    }
  ];

  async function applyTemplate(id: string) {
    setOpenMenu(null);
    const tpl = NOTE_TEMPLATES.find((t) => t.id === id);
    if (!tpl || !ref.current) return;
    // Se o editor está vazio (só com <p><br></p> ou similar), substitui;
    // senão insere no caret. Comportamento esperado pra usuário: começar
    // anotação nova com o template, mas não destruir o que ele já digitou.
    const text = ref.current.textContent?.trim() ?? "";
    if (text.length === 0) {
      ref.current.innerHTML = tpl.html();
      emitChange();
      ref.current.focus();
    } else {
      const ok = await openConfirm({
        title: "Inserir template?",
        message: "A anotação não está vazia. Quer inserir o template no fim do que já tem? Não vamos apagar nada.",
        confirmLabel: "Inserir no fim",
        cancelLabel: "Cancelar"
      });
      if (!ok) return;
      // Move caret pro fim antes de inserir.
      ref.current.focus();
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      exec("insertHTML", tpl.html());
    }
  }

  // ----- Pacote 2: imagens / tabelas / LaTeX -----

  // Embute imagem como data URL. Limite de 2MB pra evitar inflar a nota.
  const IMAGE_MAX_BYTES = 2 * 1024 * 1024;

  async function insertImageFromFile(file: File) {
    if (file.size > IMAGE_MAX_BYTES) {
      await openAlert({
        title: "Imagem grande demais",
        message: "Mantenha imagens abaixo de 2 MB para não pesar suas anotações. Você pode comprimir antes de colar.",
        confirmLabel: "Entendi"
      });
      return;
    }
    const url = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Falha ao ler imagem"));
      reader.readAsDataURL(file);
    }).catch(() => null);
    if (!url) return;
    exec("insertImage", url);
  }

  function pickImage() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);
    input.onchange = () => {
      const f = input.files?.[0];
      input.remove();
      if (f) void insertImageFromFile(f);
    };
    input.click();
  }

  // Cola imagem (Ctrl+V de print screen, drag de outro programa). Outros
  // tipos de cola continuam usando o handler nativo do contentEditable.
  function onPaste(e: ClipboardEvent) {
    if (!e.clipboardData) return;
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.type && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (!f) continue;
        e.preventDefault();
        void insertImageFromFile(f);
        return;
      }
    }
  }

  async function insertTable() {
    // Salva a posição do caret ANTES do modal abrir — o openPrompt rouba o
    // foco e a seleção do contentEditable, então sem isso a tabela é
    // inserida no início do editor (ou nem é inserida).
    const saved = saveSelection();
    const dims = await openPrompt({
      title: "Inserir tabela",
      message: "Tamanho da tabela no formato linhas x colunas (máx 10x10).",
      defaultValue: "3x3",
      placeholder: "ex: 3x4",
      confirmLabel: "Inserir"
    });
    if (!dims) return;
    const m = dims.trim().match(/^(\d{1,2})\s*[x×]\s*(\d{1,2})$/i);
    if (!m) {
      await openAlert({
        title: "Formato inválido",
        message: 'Use o formato "linhas x colunas". Exemplo: 3x4.',
        confirmLabel: "Entendi"
      });
      return;
    }
    const rows = Math.min(Math.max(parseInt(m[1], 10), 1), 10);
    const cols = Math.min(Math.max(parseInt(m[2], 10), 1), 10);
    let html = '<table class="note-table"><thead><tr>';
    for (let c = 0; c < cols; c++) html += `<th>Col ${c + 1}</th>`;
    html += "</tr></thead><tbody>";
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) html += "<td>&nbsp;</td>";
      html += "</tr>";
    }
    html += "</tbody></table><p><br></p>";
    restoreSelection(saved);
    exec("insertHTML", html);
  }

  // LaTeX inline: span contenteditable=false guarda o source em data-tex,
  // KaTeX renderiza o conteúdo. Clique no span abre prompt pra reeditar.
  function renderMathInsideEditor() {
    void ensureKaTeX().then(() => {
      if (!ref.current) return;
      renderMathIn(ref.current);
    });
  }

  async function insertMath() {
    // saveSelection pra sobreviver ao modal (mesma razão do insertTable).
    const saved = saveSelection();
    const tex = await openPrompt({
      title: "Inserir fórmula LaTeX",
      message: "Digite a fórmula em LaTeX. Exemplos: \\frac{1}{2}, x^2 + y^2 = z^2, \\int_0^1 x\\,dx",
      placeholder: "\\frac{a}{b}",
      confirmLabel: "Inserir"
    });
    if (!tex || !tex.trim()) return;
    const src = tex.trim();
    const safe = escapeAttr(src);
    // `\(` é o delimitador inline do KaTeX; data-tex guarda o source pra
    // reedição. Espaço em branco depois pra deixar caret após o span.
    const html = `<span class="math-inline" data-tex="${safe}" contenteditable="false">\\(${escapeHtml(src)}\\)</span>&nbsp;`;
    restoreSelection(saved);
    exec("insertHTML", html);
    renderMathInsideEditor();
  }

  async function editMathSpan(span: Element) {
    const current = span.getAttribute("data-tex") || "";
    const next = await openPrompt({
      title: "Editar fórmula",
      message: "Editar LaTeX. Apague tudo para remover a fórmula.",
      defaultValue: current,
      placeholder: "\\frac{a}{b}",
      confirmLabel: "Salvar"
    });
    if (next == null) return;
    if (!next.trim()) {
      span.remove();
      emitChange();
      return;
    }
    const safe = escapeAttr(next.trim());
    span.setAttribute("data-tex", safe);
    span.innerHTML = `\\(${escapeHtml(next.trim())}\\)`;
    renderMathInsideEditor();
    emitChange();
  }

  // Clique no editor: alterna checkbox no .task-check OU abre prompt de
  // re-edição quando clica numa fórmula LaTeX.
  function onEditorClick(e: MouseEvent) {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const check = target.closest(".task-check");
    if (check) {
      e.preventDefault();
      const checked = check.getAttribute("data-checked") === "true";
      const next = checked ? "false" : "true";
      check.setAttribute("data-checked", next);
      check.setAttribute("aria-checked", next);
      check.textContent = checked ? "☐" : "✅";
      const li = check.closest("li");
      if (li) li.setAttribute("data-checked", next);
      emitChange();
      return;
    }
    const math = target.closest(".math-inline");
    if (math) {
      e.preventDefault();
      void editMathSpan(math);
      return;
    }
    // Click em <a> abre popover com URL + ações (Ctrl/Cmd+click navega nativo).
    const anchor = target.closest("a[href]");
    if (anchor && anchor instanceof HTMLAnchorElement) {
      // Ctrl/Cmd+click → comportamento nativo (abrir em nova aba)
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      const rect = anchor.getBoundingClientRect();
      const PADDING = 8;
      const x = Math.max(PADDING, Math.min(window.innerWidth - 280, rect.left));
      const y = Math.max(PADDING, rect.top - 48);
      setLinkPopover({
        x,
        y,
        href: anchor.getAttribute("href") || "",
        anchorEl: anchor
      });
      return;
    }
  }

  // Atalhos de teclado (Word/Docs-like). Negrito/itálico/sublinhado o browser
  // já intercepta nativamente — refreshActive cuida da toolbar via selectionchange.
  function onKeyDown(e: KeyboardEvent) {
    // Enter dentro de uma task-list cria um NOVO item com o checkbox em vez
    // de só uma <li> vazia (default do contentEditable). Se o item atual
    // está vazio (só com o span), Enter "sai" da lista pra um parágrafo —
    // mesmo comportamento de listas normais. [[project-notes-system-caderno]]
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      const sel = window.getSelection();
      if (sel && sel.anchorNode) {
        const li = (sel.anchorNode instanceof Element
          ? sel.anchorNode
          : sel.anchorNode.parentElement
        )?.closest("li");
        if (li && li.parentElement?.classList.contains("task-list")) {
          // Texto do li sem o span do check: se vazio, sai da lista.
          const liText = li.textContent?.replace(/[☐✅]/g, "").trim() ?? "";
          if (liText.length === 0) {
            e.preventDefault();
            exec("formatBlock", "P");
            return;
          }
          // Senão, insere uma nova li com o check no próximo nível.
          e.preventDefault();
          exec(
            "insertHTML",
            '<li data-checked="false"><span class="task-check" data-checked="false" contenteditable="false" role="checkbox" aria-checked="false" aria-label="Marcar item">☐</span>&nbsp;</li>'
          );
          return;
        }
      }
    }

    const ctrl = e.ctrlKey || e.metaKey;
    if (!ctrl) return;
    const k = e.key.toLowerCase();
    if (e.shiftKey) {
      if (k === "1") { e.preventDefault(); execFormatToggle("H1"); return; }
      if (k === "2") { e.preventDefault(); execFormatToggle("H2"); return; }
      if (k === "3") { e.preventDefault(); execFormatToggle("H3"); return; }
      if (k === "7") { e.preventDefault(); exec("insertOrderedList"); return; }
      if (k === "8") { e.preventDefault(); exec("insertUnorderedList"); return; }
      if (k === "9") { e.preventDefault(); execFormatToggle("BLOCKQUOTE"); return; }
      if (k === "x") { e.preventDefault(); exec("strikeThrough"); return; }
      return;
    }
    if (k === "k") { e.preventDefault(); insertLink(); return; }
  }

  // Classes base. Active state via data-active="true" → ver .rt-tb-btn em globals.css.
  const tbBtn = "rt-tb-btn grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors";
  const tbBtnSm = "rt-tb-btn flex h-7 items-center gap-1 rounded px-1.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground transition-colors";

  return (
    <>
      <div class="flex shrink-0 flex-wrap items-center gap-0.5 border-b border-border bg-card/30 px-1.5 py-1">
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} title="Negrito (Ctrl+B)" aria-label="Negrito" data-active={active.bold ? "true" : "false"} class={tbBtn}>
          <Bold className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} title="Itálico (Ctrl+I)" aria-label="Itálico" data-active={active.italic ? "true" : "false"} class={tbBtn}>
          <Italic className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("underline")} title="Sublinhado (Ctrl+U)" aria-label="Sublinhado" data-active={active.underline ? "true" : "false"} class={tbBtn}>
          <Underline className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("strikeThrough")} title="Tachado (Ctrl+Shift+X)" aria-label="Tachado" data-active={active.strike ? "true" : "false"} class={tbBtn}>
          <Strikethrough className="size-3.5" />
        </button>

        {/* Cor do texto */}
        <div class="relative" data-rt-menu>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpenMenu(openMenu === "color" ? null : "color")} title="Cor do texto" aria-label="Cor do texto" aria-haspopup="true"  class={tbBtnSm}>
            <Palette className="size-3.5" />
            <ChevronDown className="size-3" />
          </button>
          {openMenu === "color" && (
            <div class="absolute left-0 top-full z-50 mt-1 w-[10rem] rounded-md border border-border bg-popover p-2 shadow-lg">
              <div class="grid grid-cols-4 gap-1.5">
                {TEXT_COLOR_PALETTE.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyColor(c.value)}
                    title={c.label}
                    aria-label={c.label}
                    class="rt-swatch"
                    style={c.value ? `background:${c.value}` : "background:repeating-linear-gradient(45deg,#e5e7eb 0 4px,#fff 4px 8px)"}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Marca-texto */}
        <div class="relative" data-rt-menu>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpenMenu(openMenu === "highlight" ? null : "highlight")} title="Marca-texto" aria-label="Marca-texto" aria-haspopup="true"  class={tbBtnSm}>
            <Highlighter className="size-3.5" />
            <ChevronDown className="size-3" />
          </button>
          {openMenu === "highlight" && (
            <div class="absolute left-0 top-full z-50 mt-1 w-[10rem] rounded-md border border-border bg-popover p-2 shadow-lg">
              <div class="grid grid-cols-4 gap-1.5">
                {HIGHLIGHT_PALETTE.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyHighlight(c.value)}
                    title={c.label}
                    aria-label={c.label}
                    class="rt-swatch"
                    style={c.value ? `background:${c.value}` : "background:repeating-linear-gradient(45deg,#e5e7eb 0 4px,#fff 4px 8px)"}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div class="mx-1 h-5 w-px bg-border/60" />

        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execFormatToggle("H1")} title="Título 1 (clique de novo pra desfazer · Ctrl+Shift+1)" aria-label="Título 1" data-active={active.h1 ? "true" : "false"} class={tbBtn}>
          <Heading1 className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execFormatToggle("H2")} title="Título 2 (clique de novo pra desfazer · Ctrl+Shift+2)" aria-label="Título 2" data-active={active.h2 ? "true" : "false"} class={tbBtn}>
          <Heading2 className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execFormatToggle("H3")} title="Título 3 (clique de novo pra desfazer · Ctrl+Shift+3)" aria-label="Título 3" data-active={active.h3 ? "true" : "false"} class={tbBtn}>
          <Heading3 className="size-3.5" />
        </button>
        {/* ¶ removido — pra voltar pra texto normal o aluno clica no botão
            de bloco já ativo (toggle) OU usa "Limpar formatação". Ter 3
            caminhos pro mesmo comando confundia. */}
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => void clearFormatting()} title="Limpar formatação (negrito, cor, link, título, lista — volta pra texto normal)" aria-label="Limpar formatação" class={tbBtn}>
          <RemoveFormatting className="size-3.5" />
        </button>

        <div class="mx-1 h-5 w-px bg-border/60" />

        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")} title="Lista (Ctrl+Shift+8)" aria-label="Lista" data-active={active.ul ? "true" : "false"} class={tbBtn}>
          <List className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertOrderedList")} title="Lista numerada (Ctrl+Shift+7)" aria-label="Lista numerada" data-active={active.ol ? "true" : "false"} class={tbBtn}>
          <ListOrdered className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={insertChecklist} title="Lista de tarefas" aria-label="Lista de tarefas" class={tbBtn}>
          <ListChecks className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => execFormatToggle("BLOCKQUOTE")} title="Citação (clique de novo pra desfazer · Ctrl+Shift+9)" aria-label="Citação" data-active={active.blockquote ? "true" : "false"} class={tbBtn}>
          <Quote className="size-3.5" />
        </button>

        <div class="mx-1 h-5 w-px bg-border/60" />

        {/* Callouts */}
        <div class="relative" data-rt-menu>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpenMenu(openMenu === "callout" ? null : "callout")} title="Caixa de destaque" aria-label="Caixa de destaque" aria-haspopup="true"  class={tbBtnSm}>
            <Info className="size-3.5" />
            <ChevronDown className="size-3" />
          </button>
          {openMenu === "callout" && (
            <div class="absolute left-0 top-full z-50 mt-1 min-w-[10rem] rounded-md border border-border bg-popover p-1 shadow-lg">
              {CALLOUT_PRESETS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertCallout(c.id)}
                  class="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-accent"
                >
                  <span aria-hidden="true">{c.icon}</span>
                  <span>{c.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={insertHr} title="Linha divisória" aria-label="Linha divisória" class={tbBtn}>
          <Minus className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => void insertLink()} title="Inserir link (Ctrl+K)" aria-label="Inserir link" class={tbBtn}>
          <LinkIcon className="size-3.5" />
        </button>

        <div class="mx-1 h-5 w-px bg-border/60" />

        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={pickImage} title="Inserir imagem (ou cole com Ctrl+V)" aria-label="Inserir imagem" class={tbBtn}>
          <ImageIcon className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => void insertTable()} title="Inserir tabela" aria-label="Inserir tabela" class={tbBtn}>
          <TableIcon className="size-3.5" />
        </button>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => void insertMath()} title="Inserir fórmula LaTeX" aria-label="Inserir fórmula" class={tbBtn}>
          <Sigma className="size-3.5" />
        </button>

        <div class="mx-1 h-5 w-px bg-border/60" />

        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={insertDateTime} title="Inserir data e hora atual" aria-label="Inserir data e hora" class={tbBtn}>
          <CalendarClock className="size-3.5" />
        </button>

        {/* Templates */}
        <div class="relative" data-rt-menu>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpenMenu(openMenu === "template" ? null : "template")} title="Inserir template" aria-label="Inserir template" aria-haspopup="true" class={tbBtnSm}>
            <FileText className="size-3.5" />
            <ChevronDown className="size-3" />
          </button>
          {openMenu === "template" && (
            // left-0 evita overflow esquerdo quando a toolbar quebra em 2 linhas
            // (a janela popup é estreita o suficiente pra wrap acontecer).
            <div class="absolute left-0 top-full z-50 mt-1 min-w-[14rem] rounded-md border border-border bg-popover p-1 shadow-lg">
              {NOTE_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void applyTemplate(t.id)}
                  class="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-accent"
                >
                  <span aria-hidden="true">{t.emoji}</span>
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div class="relative flex-1 overflow-y-auto">
        {isEmpty && placeholder && (
          <p class="pointer-events-none absolute left-3 top-2 text-[11px] italic text-muted-foreground/60">
            {placeholder}
          </p>
        )}
        <div
          ref={ref}
          contentEditable
          spellcheck
          onInput={emitChange}
          onBlur={emitChange}
          onClick={onEditorClick}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          aria-label="Editor de anotação"
          class="prose-question h-full min-h-[8rem] px-3 py-2 text-sm leading-relaxed outline-none focus:outline-none"
        />
      </div>
      {linkPopover && (
        <div
          data-rt-link-popover
          class="fixed z-50 flex max-w-[min(380px,calc(100vw-1rem))] items-center gap-1 rounded-md border border-border bg-popover px-2 py-1 shadow-lg"
          style={`left:${linkPopover.x}px;top:${linkPopover.y}px`}
        >
          <a
            href={linkPopover.href}
            target="_blank"
            rel="noopener noreferrer"
            title={linkPopover.href}
            class="min-w-0 max-w-[200px] flex-1 truncate text-[11px] font-medium text-primary underline hover:text-primary/80"
          >
            {linkPopover.href}
          </a>
          <button
            type="button"
            onClick={() => {
              window.open(linkPopover.href, "_blank", "noopener,noreferrer");
              setLinkPopover(null);
            }}
            title="Abrir em nova aba"
            aria-label="Abrir"
            class="grid size-6 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ExternalLink className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => void editLinkHref(linkPopover.anchorEl)}
            title="Editar URL"
            aria-label="Editar"
            class="grid size-6 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <LinkIcon className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => removeLink(linkPopover.anchorEl)}
            title="Remover link (mantém texto)"
            aria-label="Remover link"
            class="grid size-6 place-items-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      )}
      {aiBubble && (
        <div
          // position: fixed flutua sobre o viewport — não interfere no layout
          // do scroll do editor. zIndex alto pra ficar acima de tudo, mas
          // sem block pointer events fora dela.
          data-rt-ai-bubble
          // onMouseDown.preventDefault preserva a seleção quando o aluno
          // clica num dos botões da bolha (mesmo princípio dos botões da toolbar).
          onMouseDown={(e) => e.preventDefault()}
          class="fixed z-50 flex items-center gap-0.5 rounded-full border border-border bg-popover px-1 py-1 shadow-lg"
          style={`left:${aiBubble.x}px;top:${aiBubble.y}px`}
        >
          <button
            type="button"
            onClick={() => void runAiAction("explain")}
            disabled={!!aiBusy}
            title="Explique este trecho"
            class="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-foreground hover:bg-accent disabled:cursor-wait disabled:opacity-60"
          >
            {aiBusy === "explain" ? <Loader2 className="size-3 animate-spin" /> : <Sparkles className="size-3" />}
            Explique
          </button>
          <button
            type="button"
            onClick={() => void runAiAction("summarize")}
            disabled={!!aiBusy}
            title="Resumir trecho selecionado"
            class="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-foreground hover:bg-accent disabled:cursor-wait disabled:opacity-60"
          >
            {aiBusy === "summarize" ? <Loader2 className="size-3 animate-spin" /> : <StickyNote className="size-3" />}
            Resumir
          </button>
          <button
            type="button"
            onClick={() => void runAiAction("improve")}
            disabled={!!aiBusy}
            title="Melhorar a escrita do trecho"
            class="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-foreground hover:bg-accent disabled:cursor-wait disabled:opacity-60"
          >
            {aiBusy === "improve" ? <Loader2 className="size-3 animate-spin" /> : <SquarePen className="size-3" />}
            Melhorar
          </button>
          <button
            type="button"
            onClick={() => void runAiAction("flashcards")}
            disabled={!!aiBusy}
            title="Gerar flashcards (Pergunta/Resposta)"
            class="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-foreground hover:bg-accent disabled:cursor-wait disabled:opacity-60"
          >
            {aiBusy === "flashcards" ? <Loader2 className="size-3 animate-spin" /> : <ListChecks className="size-3" />}
            Flashcards
          </button>
        </div>
      )}
    </>
  );
}
